import { Controller } from '@hotwired/stimulus';

const MODES = ['watch', 'learn', 'normal'];
const STORAGE_KEY = 'bs:tutorial-view-mode';

// Tutorial show page "Density Dial" — Watch | Learn | Normal.
//
// Switching modes only flips `data-mode` on the root element; the layout is pure CSS. Nothing is
// re-rendered and nothing moves in the DOM, so the player iframe keeps playing across a switch.
export default class extends Controller {
  static targets = ['option', 'body', 'actions'];
  static values = {
    mode: { type: String, default: 'normal' },
    // True when the server rendered a signed-in user's saved preference.
    persisted: { type: Boolean, default: false },
    url: String
  };

  connect() {
    // The server already painted the right mode for signed-in users and for `?view=` links.
    // Anonymous visitors get "normal" from the server, so restore their localStorage choice here.
    if (!this.persistedValue && !this.hasViewParam()) {
      const stored = this.readStoredMode();
      if (stored && stored !== this.modeValue) this.modeValue = stored;
    }
    this.onResize = () => this.measureWatchCap();
    window.addEventListener('resize', this.onResize, { passive: true });

    this.render();
  }

  disconnect() {
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.persistTimer);
  }

  select(event) {
    const mode = event.params.mode;
    if (!MODES.includes(mode)) return;
    this.modeValue = mode;
  }

  // Arrow keys move between the three options, per the toggle's role="group" semantics.
  navigate(event) {
    const keys = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -1, ArrowDown: 1 };
    const step = keys[event.key];
    if (step === undefined) return;

    event.preventDefault();
    const buttons = this.optionTargets;
    const current = buttons.indexOf(event.currentTarget);
    if (current === -1) return;

    const next = buttons[(current + step + buttons.length) % buttons.length];
    next.focus();
    next.click();
  }

  modeValueChanged(mode, previous) {
    this.render();
    if (previous === undefined || mode === previous) return;

    this.storeMode(mode);
    this.persistMode(mode);
  }

  render() {
    const mode = MODES.includes(this.modeValue) ? this.modeValue : 'normal';
    this.element.dataset.mode = mode;

    this.optionTargets.forEach((button) => {
      const active = button.dataset.tutorialViewModeParam === mode;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.measureWatchCap();
  }

  // Watch caps the player by WIDTH (a max-height on an aspect-ratio box squashes it), sized so the
  // action rail still clears the fold. The CSS fallback assumes 200px of chrome; here we measure
  // what the site header, context bar and rail actually take so the promise holds on any header.
  measureWatchCap() {
    if (!this.hasBodyTarget) return;
    if (this.modeValue !== 'watch') {
      this.element.style.removeProperty('--tut-watch-width');
      return;
    }

    // Only measure things that do NOT depend on the cap itself — the rail's own height does
    // (it is capped to the same width), and feeding it back would collapse the layout.
    const bodyTop = this.bodyTarget.getBoundingClientRect().top + window.scrollY;
    const railAndBreathingRoom = 106;
    const chrome = Math.round(bodyTop + railAndBreathingRoom);
    const width = Math.max(480, ((window.innerHeight - chrome) * 16) / 9);

    this.element.style.setProperty('--tut-watch-width', `${Math.round(width)}px`);
  }

  hasViewParam() {
    const value = new URLSearchParams(window.location.search).get('view');
    return MODES.includes(value);
  }

  readStoredMode() {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return MODES.includes(stored) ? stored : null;
    } catch (error) {
      return null;
    }
  }

  storeMode(mode) {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (error) {
      // Private browsing / storage disabled — the server preference still covers signed-in users.
    }
  }

  // Debounced: clicking through the dial should leave one write, not one per stop along the way.
  persistMode(mode) {
    if (!this.persistedValue || !this.hasUrlValue) return;

    clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => this.postMode(mode), 400);
  }

  postMode(mode) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(this.urlValue, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { 'X-CSRF-Token': token } : {})
      },
      body: JSON.stringify({ mode })
    }).catch(() => {
      // Preference is best-effort; localStorage already has the choice for this browser.
    });
  }
}
